/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import flow from 'lodash.flow';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { ConfusionAggregate } from 'src/luis/projects/testPane/utils/batchTestResultsCalculator';

type DatasetResultItemStatsProps = {
    /**
     * The model name to display.
     */
    modelName: string;

    modelConfusions: ConfusionAggregate;
};

const messages = defineMessages({
    modelTitle: {
        id: 'testPane.batchTestResultItemStats.modelTitle',
        defaultMessage: "Model '{ modelName }' Statistics"
    },
    precision: {
        id: 'testPane.batchTestResultItemStats.precision',
        defaultMessage: 'Precision:'
    },
    recall: {
        id: 'testPane.batchTestResultItemStats.recall',
        defaultMessage: 'Recall:'
    },
    fScore: {
        id: 'testPane.batchTestResultItemStats.fScore',
        defaultMessage: 'F-Score:'
    }
});

const theme = getScopedTheme('BatchTestResultItemStats');

/**
 * The statistics (precision, recall, etc) for the
 * given model.
 */
export const BatchTestResultItemStats = (props: DatasetResultItemStatsProps) => {
    const { modelName, modelConfusions } = props;
    const { localize } = useLocalization();

    const precision = getPrecision(modelConfusions);
    const recall = getRecall(modelConfusions);
    const fScore = getFScore(precision, recall);

    return (
        <Stack tokens={{ childrenGap: 20 }}>
            <LuisText variant="xLarge">{localize(messages.modelTitle, { modelName })}</LuisText>

            <Stack horizontal horizontalAlign="space-between">
                <LuisText variant="mediumPlus" fontWeight={theme.statFontWeight}>
                    {localize(messages.precision)} {precision}
                </LuisText>

                <LuisText variant="mediumPlus" fontWeight={theme.statFontWeight}>
                    {localize(messages.recall)} {recall}
                </LuisText>

                <LuisText variant="mediumPlus" fontWeight={theme.statFontWeight}>
                    {localize(messages.fScore)} {fScore}
                </LuisText>
            </Stack>
        </Stack>
    );
};

/**
 * Calculates the precision from the given confusions.
 *
 * @param confusions The confusions for the currently
 * selected model.
 * @returns The precision as a number
 */
const getPrecision = (confusions: ConfusionAggregate) => flow(() => confusions.tp / (confusions.tp + confusions.fp), round, isNanFormat)();

/**
 * Calculates the recall from the given confusions.
 *
 * @param confusions The confusions for the currently
 * selected model.
 * @returns The recall as a number
 */
const getRecall = (confusions: ConfusionAggregate) => flow(() => confusions.tp / (confusions.tp + confusions.fn), round, isNanFormat)();

/**
 * Gets the FScore given the precision and recall.
 *
 * @param precision The precision value.
 * @param recall The recall value.
 * @returns The calculated F-score
 */
const getFScore = (precision: number | string, recall: number | string) =>
    flow(
        () => typeof precision !== 'string' && typeof recall !== 'string' && (2 * precision * recall) / (precision + recall),
        round,
        isNanFormat
    )();

/**
 * Rounds the number to the nearest digit.
 *
 * @param val The value to round.
 * @returns The rounded value.
 */
const round = (val: number): number => Math.round(val * 100) / 100;

/**
 * Checks if the value is not a number or not. If not
 * returns an appropriate string to indicate so.
 *
 * @param val The value to check
 * @returns The number itself if it is
 * a number and a string if it is not.
 */
const isNanFormat = (val: number): number | string => (isNaN(val) ? 'Not applicable' : val);
